import React, { useState, useEffect, useRef } from 'react';
import {
  Section,
  CanvasElement,
  KeyItem,
  ElementType,
  serializeSectionsToHtml,
  parseHtmlToSections,
  getDefaultSampleSections,
} from './templateSerializer';
import CanvasPanel from './CanvasPanel';
import KeyListPanel from './KeyListPanel';
import ElementConfigModal from './ElementConfigModal';

interface TemplateBuilderProps {
  value: string;
  onChange: (htmlValue: string) => void;
  keys: KeyItem[];
  isLoadingKeys: boolean;
}

const TemplateBuilder: React.FC<TemplateBuilderProps> = ({
  value,
  onChange,
  keys,
  isLoadingKeys,
}) => {
  const [mode, setMode] = useState<'visual' | 'code'>('visual');
  const [sections, setSections] = useState<Section[]>([]);
  const [rawHtml, setRawHtml] = useState<string>(value || '');
  const lastEmittedHtmlRef = useRef<string>('');

  // Element Config Modal State
  const [configModalShow, setConfigModalShow] = useState(false);
  const [editingElement, setEditingElement] = useState<CanvasElement | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [editingMaxCols, setEditingMaxCols] = useState<number>(2);

  // Synchronize internal sections and rawHtml from value prop on mount / value update
  useEffect(() => {
    setRawHtml(value || '');

    if (!value || !value.trim()) {
      if (keys && keys.length > 0 && sections.length === 0) {
        const initial = getDefaultSampleSections(keys);
        setSections(initial);
        const generatedHtml = serializeSectionsToHtml(initial);
        lastEmittedHtmlRef.current = generatedHtml;
        onChange(generatedHtml);
      }
      return;
    }

    // Only parse if value came from outside (e.g. initial load or parent update)
    if (value !== lastEmittedHtmlRef.current) {
      const parsed = parseHtmlToSections(value, keys);
      if (parsed && parsed.length > 0) {
        setSections(parsed);
      }
      lastEmittedHtmlRef.current = value;
    }
  }, [value, keys]);

  // Update Sections and trigger serialization
  const handleUpdateSections = (newSections: Section[]) => {
    setSections(newSections);
    const htmlOutput = serializeSectionsToHtml(newSections);
    setRawHtml(htmlOutput);
    lastEmittedHtmlRef.current = htmlOutput;
    onChange(htmlOutput);
  };

  // Handle adding key to active section or creating a new section if none exists
  const handleAddKeyToCanvas = (keyItem: KeyItem) => {
    let targetSections = [...sections];

    if (targetSections.length === 0) {
      const newSec: Section = {
        id: `sec-${Date.now()}`,
        title: 'SECTION 1',
        cols: 6,
        gap: 'gap-6',
        marginBottom: 'mb-4',
        showDivider: false,
        items: [],
      };
      targetSections.push(newSec);
    }

    const lastSec = targetSections[targetSections.length - 1];
    const isPhoto = (keyItem.keyType || '').toLowerCase() === 'photo';
    const newItem: CanvasElement = {
      id: `elem-${Date.now()}`,
      type: 'key',
      colSpan: isPhoto ? 6 : 3,
      keyId: keyItem.id || undefined,
      keyCode: keyItem.code,
      keyName: keyItem.name,
      keyType: keyItem.keyType,
      showLabel: true,
      photoHeight: 'h-64',
      photoFit: 'object-cover',
      photoRounded: 'rounded-xl',
      galleryCols: 3,
    };

    const updatedSections = targetSections.map((sec) =>
      sec.id === lastSec.id ? { ...sec, items: [...sec.items, newItem] } : sec
    );

    handleUpdateSections(updatedSections);
  };

  // Handle adding custom element (heading, text, divider)
  const handleAddCustomElement = (type: ElementType) => {
    let targetSections = [...sections];

    if (targetSections.length === 0) {
      const newSec: Section = {
        id: `sec-${Date.now()}`,
        title: 'SECTION 1',
        cols: 6,
        gap: 'gap-6',
        marginBottom: 'mb-4',
        showDivider: false,
        items: [],
      };
      targetSections.push(newSec);
    }

    const lastSec = targetSections[targetSections.length - 1];
    let newItem: CanvasElement;

    if (type === 'heading') {
      newItem = {
        id: `elem-${Date.now()}`,
        type: 'heading',
        colSpan: lastSec.cols,
        content: 'NEW SECTION TITLE',
        headingLevel: 'h2',
        uppercase: true,
      };
    } else if (type === 'text') {
      newItem = {
        id: `elem-${Date.now()}`,
        type: 'text',
        colSpan: 1,
        content: 'Custom text information block...',
        textSize: 'text-sm',
        fontWeight: 'font-normal',
      };
    } else {
      // Divider
      newItem = {
        id: `elem-${Date.now()}`,
        type: 'divider',
        colSpan: lastSec.cols,
      };
    }

    const updatedSections = targetSections.map((sec) =>
      sec.id === lastSec.id ? { ...sec, items: [...sec.items, newItem] } : sec
    );

    handleUpdateSections(updatedSections);
  };

  const handleAddNewSection = () => {
    const newSec: Section = {
      id: `sec-${Date.now()}`,
      title: `SECTION ${sections.length + 1}`,
      cols: 6,
      gap: 'gap-6',
      marginBottom: 'mb-4',
      showDivider: sections.length > 0,
      items: [],
    };
    handleUpdateSections([...sections, newSec]);
  };

  const handleLoadSample = () => {
    const sample = getDefaultSampleSections(keys);
    handleUpdateSections(sample);
  };

  // Handle Edit Element Modal
  const handleOpenEditElement = (element: CanvasElement, sectionId: string, maxCols: number) => {
    setEditingElement(element);
    setEditingSectionId(sectionId);
    setEditingMaxCols(maxCols);
    setConfigModalShow(true);
  };

  const handleSaveElementConfig = (updatedElement: CanvasElement) => {
    if (!editingSectionId) return;
    const updatedSections = sections.map((sec) => {
      if (sec.id === editingSectionId) {
        return {
          ...sec,
          items: sec.items.map((item) => (item.id === updatedElement.id ? updatedElement : item)),
        };
      }
      return sec;
    });
    handleUpdateSections(updatedSections);
    setConfigModalShow(false);
    setEditingElement(null);
    setEditingSectionId(null);
  };

  const handleRawHtmlChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setRawHtml(val);
    lastEmittedHtmlRef.current = val;
    onChange(val);
    const parsed = parseHtmlToSections(val, keys);
    if (parsed && parsed.length > 0) {
      setSections(parsed);
    }
  };

  const handleSwitchMode = (newMode: 'visual' | 'code') => {
    if (newMode === 'visual') {
      const parsed = parseHtmlToSections(rawHtml, keys);
      if (parsed && parsed.length > 0) {
        setSections(parsed);
      }
    } else {
      const generated = serializeSectionsToHtml(sections);
      setRawHtml(generated);
      lastEmittedHtmlRef.current = generated;
      onChange(generated);
    }
    setMode(newMode);
  };

  return (
    <div className="w-100 mb-3 border rounded-3 bg-white p-3 shadow-xs">
      {/* BUILDER HEADER TOOLBAR */}
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 pb-3 border-bottom gap-2">
        <div>
          <h6 className="fw-bold mb-1 text-dark d-flex align-items-center">
            <i className="fas fa-cubes text-primary me-2"></i> Visual Template Builder
          </h6>
        </div>

        {/* MODE TOGGLE & QUICK ACTIONS */}
        <div className="d-flex align-items-center gap-2">
          <div className="btn-group btn-group-sm" role="group">
            <button
              type="button"
              className={`btn ${mode === 'visual' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleSwitchMode('visual')}
            >
              <i className="fas fa-paint-brush me-1"></i> Visual Canvas
            </button>
            <button
              type="button"
              className={`btn ${mode === 'code' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => handleSwitchMode('code')}
            >
              <i className="fas fa-code me-1"></i> HTML Code
            </button>
          </div>
        </div>
      </div>

      {/* BODY CONTENT DEPENDING ON MODE */}
      {mode === 'visual' ? (
        <div className="row g-3">
          {/* LEFT 50%: CANVAS PANEL */}
          <div className="col-12 col-lg-6">
            <CanvasPanel
              sections={sections}
              onUpdateSections={handleUpdateSections}
              onEditElement={handleOpenEditElement}
              onLoadSample={handleLoadSample}
            />
          </div>

          {/* RIGHT 50%: KEY LIST PANEL */}
          <div className="col-12 col-lg-6">
            <KeyListPanel
              keys={keys}
              isLoadingKeys={isLoadingKeys}
              onAddKeyToCanvas={handleAddKeyToCanvas}
              onAddCustomElement={handleAddCustomElement}
              onAddNewSection={handleAddNewSection}
            />
          </div>
        </div>
      ) : (
        /* CODE VIEW MODE */
        <div className="p-2 border rounded-3 bg-light">
          <div className="d-flex justify-content-between align-items-center mb-2 px-1">
            <span className="small text-muted fw-semibold">Generated HTML Code:</span>
            <button
              type="button"
              className="btn btn-xs btn-outline-secondary"
              onClick={() => navigator.clipboard.writeText(rawHtml)}
            >
              <i className="fas fa-copy me-1"></i> Copy Code
            </button>
          </div>
          <textarea
            className="form-control form-control-compact font-monospace"
            rows={16}
            value={rawHtml}
            onChange={handleRawHtmlChange}
            style={{ fontSize: '13px', backgroundColor: '#1e293b', color: '#e2e8f0', borderRadius: '8px' }}
          />
        </div>
      )}

      {/* ELEMENT CONFIG MODAL */}
      <ElementConfigModal
        show={configModalShow}
        element={editingElement}
        maxCols={editingMaxCols}
        onSave={handleSaveElementConfig}
        onClose={() => {
          setConfigModalShow(false);
          setEditingElement(null);
        }}
      />
    </div>
  );
};

export default TemplateBuilder;
